import { useCallback, useSyncExternalStore } from 'react';

/** How navigation works on a phone: the bottom tab bar, or a hamburger button that opens a drawer. */
export type PhoneNavStyle = 'tabs' | 'drawer';

const KEY = 'phoneNavStyle';
const listeners = new Set<() => void>();

// Per-device convenience only: storage can be blocked or empty, so every access is guarded.
function read(): PhoneNavStyle {
  try {
    return window.localStorage.getItem(KEY) === 'drawer' ? 'drawer' : 'tabs';
  } catch {
    return 'tabs';
  }
}

function write(style: PhoneNavStyle) {
  try {
    window.localStorage.setItem(KEY, style);
  } catch {
    /* blocked storage: the choice just lasts until reload */
    memory = style;
  }
  listeners.forEach((l) => l());
}

let memory: PhoneNavStyle | null = null;
const current = (): PhoneNavStyle => memory ?? read();

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

export function useNavStyle(): [PhoneNavStyle, (s: PhoneNavStyle) => void] {
  const style = useSyncExternalStore(subscribe, current, () => 'tabs' as PhoneNavStyle);
  const set = useCallback((s: PhoneNavStyle) => {
    memory = null;
    write(s);
  }, []);
  return [style, set];
}
